import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, fromEvent } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { PcrReportingTask, PcrTaskStatus, PcrTaskType } from 'src/app/api/models';
import { PcrService } from 'src/app/api/services';
import { PaginationDataSource } from 'src/app/model/pagination-data-source';

class PcrTaskQuery {
  status: PcrTaskStatus[];
  task_type: PcrTaskType[];
}

@Component({
  selector: 'app-pcr-tasks-list-page',
  templateUrl: './pcr-tasks-list-page.component.html',
  styleUrls: ['./pcr-tasks-list-page.component.scss']
})
export class PcrTasksListPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly ngDestroy$ = new Subject();

  @ViewChild('table', { read: ElementRef }) public matTableRef: ElementRef;

  q: PcrTaskQuery = new PcrTaskQuery();
  title = 'PCR Tasks';

  columns = [
    'created_at',
    'invoice_id',
    'task_type',
    'status',
    'updated_at'
  ];

  dataSource = new PaginationDataSource<PcrReportingTask, PcrTaskQuery>(
    (request, query) => this.pcrService.listPcrReportingTasks({
      status: query.status,
      task_type: query.task_type,
      after: request.after,
      limit: request.limit
    }),
    { status: [], task_type: [] },
    50,
  );

  constructor(
    private pcrService: PcrService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit() {
    this.activatedRoute.queryParamMap.pipe(takeUntil(this.ngDestroy$)).subscribe(params => {
      this.queryBy({
        status: params.getAll('status') as PcrTaskStatus[],
        task_type: params.getAll('task_type') as PcrTaskType[]
      });
    });
  }

  ngAfterViewInit(): void {
    fromEvent<Event>(this.matTableRef.nativeElement, 'scroll')
      .pipe(debounceTime(700))
      .subscribe((e) => this.onTableScroll(e));
  }

  updateQuery(params: Partial<PcrTaskQuery>) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: params,
      queryParamsHandling: 'merge'
    });
  }

  queryBy(params: PcrTaskQuery) {
    this.q = params;
    this.dataSource.queryBy(this.q);
  }

  reset() {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {}
    });
  }

  private onTableScroll(e: Event): void {
    // The table is the scroll's target: a scroll event does not bubble.
    const table = e.target;
    if (!(table instanceof HTMLElement)) {
      return;
    }
    const tableViewHeight = table.offsetHeight;
    const tableScrollHeight = table.scrollHeight;
    const scrollLocation = table.scrollTop;
    const scrollThreshold = 500;

    const scrollDownLimit = tableScrollHeight - tableViewHeight - scrollThreshold;
    if (scrollLocation > scrollDownLimit) {
      this.dataSource.fetchNextPage();
    }
  }

  ngOnDestroy() {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }
}
